import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge } from "../components/Shared";
import { ChatCircleDots, MagnifyingGlass } from "@phosphor-icons/react";
import { usePipeline, pipelineParams } from "../lib/pipeline";

export default function Inbox() {
  const { activePipelineSlug } = usePipeline();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/whatsapp/conversations", { params: pipelineParams(activePipelineSlug) })
      .then(({ data }) => setConvs(data || []))
      .finally(() => setLoading(false));
  }, [activePipelineSlug]);

  const filtered = !q ? convs : convs.filter((c) => {
    const u = c.user || {};
    return [u.name, u.whatsapp, u.public_id, c.last_message_preview]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q.toLowerCase()));
  });

  return (
    <>
      <PageHeader title="Bandeja de entrada" subtitle={`Conversaciones · ${activePipelineSlug === "all" ? "todos los pipelines" : activePipelineSlug}`}>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-carbon-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="inbox-search"
            placeholder="Buscar..." className="pl-7 pr-3 py-1.5 text-sm border border-carbon-200 rounded bg-white" />
        </div>
      </PageHeader>
      <PageBody>
        {loading ? (
          <div className="text-carbon-500">Cargando...</div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center text-carbon-500">
            Sin conversaciones {activePipelineSlug !== "all" ? `en el pipeline ${activePipelineSlug}` : "todavía"}.
          </Card>
        ) : (
          <div className="grid gap-2" data-testid="inbox-list">
            {filtered.map((c) => {
              const u = c.user || {};
              const pipe = c.pipeline_slug || u.pipeline_slug || "—";
              return (
                <Link
                  key={c.id}
                  to={`/users/${c.user_id}`}
                  data-testid={`inbox-row-${c.user_id}`}
                  className="block bg-white border border-carbon-200 rounded p-3 hover:border-terracotta transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-page flex items-center justify-center shrink-0">
                      <ChatCircleDots size={18} className="text-terracotta" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3 mb-0.5">
                        <div className="font-medium truncate">{u.name || u.whatsapp || "Sin nombre"}</div>
                        <div className="text-[10px] text-carbon-400 shrink-0">
                          {c.last_message_at ? new Date(c.last_message_at).toLocaleString() : ""}
                        </div>
                      </div>
                      <div className="text-xs text-carbon-500 truncate">{c.last_message_preview || "(sin mensajes)"}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge tone={pipe === "founder" ? "terracotta" : pipe === "ambassador" ? "forest" : "neutral"}>{pipe}</Badge>
                        {u.public_id && <span className="text-[10px] font-mono text-carbon-400">{u.public_id}</span>}
                        {c.unread_count > 0 && (
                          <Badge tone="signal">{c.unread_count} nuevos</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </PageBody>
    </>
  );
}
